import React, { useCallback, useMemo } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';

import type { MalleableUiPlugin, PluginConfiguration } from '../../malleableUiPluginType';

interface RemixContentProps {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	config: PluginConfiguration;
}

export const RemixContent = ({ api, config }: RemixContentProps): React.JSX.Element | null => {
	const { createRemixContentComponent } = config;
	const isModalOpen = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isRemixModalOpen || false,
	);

	const isThinking = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isThinking || false,
	);

	const remixModalMetadata = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.remixModalMetadata,
	);

	const Component = useMemo(() => createRemixContentComponent(), [createRemixContentComponent]);

	const handleModalClose = useCallback(() => {
		api?.core.actions.execute(api?.malleableUi.commands.closeRemixModal());
	}, [api]);

	const onStartThinking = useCallback(() => {
		api?.core.actions.execute(api?.malleableUi.commands.startRemixThinking());
	}, [api]);

	const onStopThinking = useCallback(
		(options?: { reopenModal?: boolean }) => {
			api?.core.actions.execute(api?.malleableUi.commands.stopRemixThinking(options));
		},
		[api],
	);

	if (!api || !Component) {
		return null;
	}

	const selectedContext = api?.aiExperience?.actions.getAISelectionContext();
	if (!selectedContext) {
		return null;
	}

	return (
		<Component
			isModalOpen={isModalOpen}
			isThinking={isThinking}
			handleModalClose={handleModalClose}
			selectedContext={selectedContext}
			editorApi={api}
			onStartThinking={onStartThinking}
			onStopThinking={onStopThinking}
			remixModalMetadata={remixModalMetadata}
		/>
	);
};
